<template>
  <div class="glass-card p-4">
    <div class="flex justify-between items-center mb-4">
      <h3 class="font-bold flex items-center gap-2">
        <div class="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v1a1 1 0 001 1h4a1 1 0 001-1v-1m3-2V8a2 2 0 00-2-2H8a2 2 0 00-2 2v6m0 4V8a2 2 0 012-2h4a2 2 0 012 2v8m0 0h2m-6 4h.01M9 17h.01"/>
          </svg>
        </div>
        Reports & Analytics
      </h3>
      <div class="flex gap-2">
        <select v-model="selectedPeriod" class="text-xs input">
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
        </select>
        <button @click="generateReport" class="accent-btn text-sm">
          Generate Report
        </button>
      </div>
    </div>

    <!-- Key Metrics -->
    <div class="grid grid-cols-2 gap-3 mb-4">
      <div class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-3 rounded-xl">
        <div class="flex justify-between items-start mb-2">
          <span class="text-2xl">📊</span>
          <span class="text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 px-2 py-1 rounded-full">
            {{ selectedPeriod }}
          </span>
        </div>
        <p class="font-bold text-lg">{{ metrics.totalOperations }}</p>
        <p class="text-xs themed-text-secondary">Total Operations</p>
        <div class="mt-2 text-xs text-green-600">↑ {{ metrics.operationsGrowth }}% from last period</div>
      </div>

      <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-3 rounded-xl">
        <div class="flex justify-between items-start mb-2">
          <span class="text-2xl">🌾</span>
          <span class="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 px-2 py-1 rounded-full">
            Coverage
          </span>
        </div>
        <p class="font-bold text-lg">{{ metrics.coveredArea }}</p>
        <p class="text-xs themed-text-secondary">Hectares Covered</p>
        <div class="mt-2 text-xs text-green-600">↑ {{ metrics.coverageGrowth }}% from last period</div>
      </div>

      <div class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-3 rounded-xl">
        <div class="flex justify-between items-start mb-2">
          <span class="text-2xl">⚡</span>
          <span class="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded-full">
            Efficiency
          </span>
        </div>
        <p class="font-bold text-lg">{{ metrics.efficiency }}%</p>
        <p class="text-xs themed-text-secondary">Operational Efficiency</p>
        <div class="mt-2 text-xs text-green-600">↑ {{ metrics.efficiencyGrowth }}% from last period</div>
      </div>

      <div class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-3 rounded-xl">
        <div class="flex justify-between items-start mb-2">
          <span class="text-2xl">💰</span>
          <span class="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300 px-2 py-1 rounded-full">
            Revenue
          </span>
        </div>
        <p class="font-bold text-lg">฿{{ metrics.revenue.toLocaleString() }}</p>
        <p class="text-xs themed-text-secondary">Total Revenue</p>
        <div class="mt-2 text-xs text-green-600">↑ {{ metrics.revenueGrowth }}% from last period</div>
      </div>
    </div>

    <!-- Chart Section -->
    <div class="mb-4">
      <h4 class="font-semibold mb-3">Performance Trends</h4>
      <div class="bg-white/5 dark:bg-black/5 p-4 rounded-xl">
        <canvas ref="chartCanvas" width="400" height="200"></canvas>
      </div>
    </div>

    <!-- Recent Reports -->
    <div class="mb-4">
      <h4 class="font-semibold mb-3">Recent Reports</h4>
      <div class="space-y-2">
        <div v-for="report in recentReports" :key="report.id" 
             class="flex justify-between items-center p-3 rounded-lg bg-white/5 dark:bg-black/5 hover:bg-white/10 dark:hover:bg-black/10 transition-colors cursor-pointer"
             @click="downloadReport(report)">
          <div class="flex-1">
            <h5 class="font-medium text-sm">{{ report.title }}</h5>
            <p class="text-xs themed-text-secondary">{{ report.description }}</p>
            <div class="flex gap-4 mt-1 text-xs themed-text-secondary">
              <span>{{ report.date }}</span>
              <span>{{ report.type }}</span>
              <span>{{ report.size }}</span>
            </div>
          </div>
          <button class="text-blue-600 hover:text-blue-800">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Report Generation Modal -->
    <div v-if="showReportModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="glass-card p-6 max-w-md w-full">
        <h3 class="text-lg font-bold mb-4">Generate Report</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">Report Type</label>
            <select v-model="reportConfig.type" class="input">
              <option value="operations">Operations Summary</option>
              <option value="field-analysis">Field Analysis</option>
              <option value="drone-performance">Drone Performance</option>
              <option value="financial">Financial Report</option>
              <option value="weather-impact">Weather Impact</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium mb-1">Period</label>
            <select v-model="reportConfig.period" class="input">
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium mb-1">Format</label>
            <select v-model="reportConfig.format" class="input">
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
              <option value="csv">CSV</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium mb-1">Include</label>
            <div class="space-y-2">
              <label class="flex items-center gap-2">
                <input type="checkbox" v-model="reportConfig.includeCharts" class="rounded">
                <span class="text-sm">Charts and Graphs</span>
              </label>
              <label class="flex items-center gap-2">
                <input type="checkbox" v-model="reportConfig.includeDetails" class="rounded">
                <span class="text-sm">Detailed Analysis</span>
              </label>
              <label class="flex items-center gap-2">
                <input type="checkbox" v-model="reportConfig.includeRecommendations" class="rounded">
                <span class="text-sm">Recommendations</span>
              </label>
            </div>
          </div>
        </div>
        
        <div class="flex justify-end gap-2 mt-6">
          <button @click="showReportModal = false" class="px-4 py-2 text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button @click="generateCustomReport" class="accent-btn">
            Generate
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'

const selectedPeriod = ref('week')
const showReportModal = ref(false)
const chartCanvas = ref(null)

const metrics = ref({
  totalOperations: 156,
  operationsGrowth: 12.5,
  coveredArea: 2847,
  coverageGrowth: 8.3,
  efficiency: 94.2,
  efficiencyGrowth: 3.7,
  revenue: 1250000,
  revenueGrowth: 15.8
})

const recentReports = ref([
  {
    id: 1,
    title: 'Weekly Operations Summary',
    description: 'Complete overview of all drone operations this week',
    date: '2024-01-20',
    type: 'Operations',
    size: '2.4 MB'
  },
  {
    id: 2,
    title: 'North Field Analysis',
    description: 'Detailed analysis of crop health and soil conditions',
    date: '2024-01-19',
    type: 'Field Analysis',
    size: '1.8 MB'
  },
  {
    id: 3,
    title: 'Drone Fleet Performance',
    description: 'Performance metrics for all drones in the fleet',
    date: '2024-01-18',
    type: 'Performance',
    size: '3.1 MB'
  }
])

const reportConfig = ref({
  type: 'operations',
  period: 'week',
  format: 'pdf',
  includeCharts: true,
  includeDetails: true,
  includeRecommendations: false
})

const generateReport = () => {
  showReportModal.value = true
}

const generateCustomReport = () => {
  // Simulate report generation
  const report = {
    id: Date.now(),
    title: `${reportConfig.value.type.charAt(0).toUpperCase() + reportConfig.value.type.slice(1)} Report - ${reportConfig.value.period}`,
    description: `Generated ${reportConfig.value.type.toUpperCase()} report for ${reportConfig.value.period}`,
    date: new Date().toISOString().split('T')[0],
    type: reportConfig.value.type.charAt(0).toUpperCase() + reportConfig.value.type.slice(1),
    size: `${(Math.random() * 3 + 1).toFixed(1)} MB`
  }
  
  recentReports.value.unshift(report)
  showReportModal.value = false
  
  // Show success message
  console.log('Report generated:', report)
}

const downloadReport = (report) => {
  // Simulate download
  console.log('Downloading report:', report)
  
  // Create a simple text file for demo
  const content = `
Report: ${report.title}
Date: ${report.date}
Type: ${report.type}
Description: ${report.description}

This is a sample report download.
In a real application, this would download the actual ${report.format} file.
  `.trim()
  
  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${report.title.replace(/\s+/g, '_')}.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

const drawChart = () => {
  if (!chartCanvas.value) return
  
  const ctx = chartCanvas.value.getContext('2d')
  const width = chartCanvas.value.width
  const height = chartCanvas.value.height
  
  // Clear canvas
  ctx.clearRect(0, 0, width, height)
  
  // Sample data
  const data = [65, 78, 82, 91, 85, 92, 88, 95, 89, 94]
  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed']
  
  const maxValue = Math.max(...data)
  const padding = 40
  const chartWidth = width - padding * 2
  const chartHeight = height - padding * 2
  const barWidth = chartWidth / data.length * 0.7
  const spacing = chartWidth / data.length
  
  // Draw axes
  ctx.strokeStyle = '#e5e7eb'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(padding, padding)
  ctx.lineTo(padding, height - padding)
  ctx.lineTo(width - padding, height - padding)
  ctx.stroke()
  
  // Draw bars
  data.forEach((value, index) => {
    const barHeight = (value / maxValue) * chartHeight
    const x = padding + index * spacing + (spacing - barWidth) / 2
    const y = height - padding - barHeight
    
    // Gradient fill
    const gradient = ctx.createLinearGradient(0, y, 0, height - padding)
    gradient.addColorStop(0, '#10b981')
    gradient.addColorStop(1, '#059669')
    
    ctx.fillStyle = gradient
    ctx.fillRect(x, y, barWidth, barHeight)
    
    // Value label
    ctx.fillStyle = '#374151'
    ctx.font = '10px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText(value, x + barWidth / 2, y - 5)
    
    // X-axis label
    ctx.fillText(labels[index], x + barWidth / 2, height - padding + 15)
  })
  
  // Title
  ctx.fillStyle = '#111827'
  ctx.font = 'bold 12px sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('Weekly Operations Trend', width / 2, 20)
}

onMounted(() => {
  nextTick(() => {
    drawChart()
  })
})
</script>
